import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  useListCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  CategoryDto,
} from "@/store/apiSlice";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit } from "lucide-react";

export default function CategoriesPage() {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: categories } = useListCategoriesQuery({
    status: statusFilter === "all" ? undefined : statusFilter,
  });
  const categoryList = Array.isArray(categories) ? categories : (categories as any)?.results ?? [];

  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryDto | null>(null);
  const [errors, setErrors] = useState<{ name?: string; api?: string }>({});
  const [form, setForm] = useState<{
    name: string;
    slug: string;
    is_published: boolean;
    is_featured: boolean;
    is_disabled: boolean;
    order: string;
  }>({
    name: "",
    slug: "",
    is_published: true,
    is_featured: false,
    is_disabled: false,
    order: "0",
  });

  const openCreateModal = () => {
    setEditing(null);
    setForm({
      name: "",
      slug: "",
      is_published: true,
      is_featured: false,
      is_disabled: false,
      order: "0",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (category: CategoryDto) => {
    setEditing(category);
    setForm({
      name: category.name,
      slug: category.slug || "",
      is_published: category.is_published,
      is_featured: category.is_featured,
      is_disabled: category.is_disabled,
      order: String(category.order ?? 0),
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setErrors({});

    const trimmedName = form.name.trim();
    const trimmedSlug = form.slug.trim();
    const orderValue = Number(form.order || 0);

    if (!trimmedName) {
      setErrors({ name: "Name is required." });
      return;
    }

    const payload = {
      name: trimmedName,
      slug: trimmedSlug || null,
      is_published: form.is_published,
      is_featured: form.is_featured,
      is_disabled: form.is_disabled,
      order: orderValue,
    };

    try {
      if (editing) {
        await updateCategory({ id: editing.id, ...payload }).unwrap();
      } else {
        await createCategory(payload).unwrap();
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("Failed to save category:", error);
      const apiData = error?.data;
      if (apiData && typeof apiData === "object") {
        const firstKey = Object.keys(apiData)[0];
        const value = (apiData as any)[firstKey];
        if (Array.isArray(value)) {
          setErrors((prev) => ({ ...prev, api: value.join(" ") }));
          return;
        }
        if (typeof value === "string") {
          setErrors((prev) => ({ ...prev, api: value }));
          return;
        }
      }
      setErrors((prev) => ({
        ...prev,
        api: "Something went wrong while saving the category.",
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">
            Manage logical content categories and their visibility.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-md border px-1 py-0.5 bg-background">
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "all" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatusFilter("all")}
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "published" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatusFilter("published")}
            >
              Published
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "unpublished" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatusFilter("unpublished")}
            >
              Unpublished
            </Button>
            <Button
              type="button"
              size="sm"
              variant={statusFilter === "disabled" ? "secondary" : "ghost"}
              className="h-8 px-2 text-xs"
              onClick={() => setStatusFilter("disabled")}
            >
              Disabled
            </Button>
          </div>

          <Button type="button" onClick={openCreateModal}>
            + Create Category
          </Button>
        </div>
      </div>

      <div className="border rounded-lg bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Order</TableHead>
              <TableHead className="w-16 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
          {categoryList.map((c: CategoryDto) => (
              <TableRow key={c.id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.slug || "-"}</TableCell>
                <TableCell>
                  <div className="flex flex-col text-xs">
                    <span>{c.is_published ? "Published" : "Unpublished"}</span>
                    <span>{c.is_featured ? "Featured" : "Not featured"}</span>
                    <span>{c.is_disabled ? "Disabled" : "Active"}</span>
                  </div>
                </TableCell>
                <TableCell>{c.order}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => openEditModal(c)}
                    aria-label="Edit category"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {categoryList.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
                  No categories found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "Create category"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Slug (optional)</label>
              <Input
                value={form.slug}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="auto-generated from name if left blank"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Published</span>
                  <Switch
                    checked={form.is_published}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_published: checked }))
                    }
                  />
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Featured</span>
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_featured: checked }))
                    }
                  />
                </label>
              </div>
              <div className="space-y-1">
                <label className="flex items-center justify-between text-sm font-medium">
                  <span>Disabled</span>
                  <Switch
                    checked={form.is_disabled}
                    onCheckedChange={(checked: boolean) =>
                      setForm((prev) => ({ ...prev, is_disabled: checked }))
                    }
                  />
                </label>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Order</label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm((prev) => ({ ...prev, order: e.target.value }))}
              />
            </div>

            {errors.api && (
              <p className="text-xs text-red-500 mt-1">{errors.api}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit}>
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
